import { FormEvent, KeyboardEvent, Ref, useState } from 'react'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import SendIcon from '@mui/icons-material/Send'
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined'
import { ASSISTANT_DISCLAIMER, ASSISTANT_NAME } from './copy'

/** The design's square send button, level with a one-line message box. */
const squareButtonSx = {
  width: 40,
  height: 40,
  flexShrink: 0,
  borderRadius: 1,
  color: 'primary.main',
  '&:hover': { bgcolor: 'secondary.light' },
} as const

interface AssistantComposerProps {
  onSend: (text: string) => void
  onStop: () => void
  /** True while an answer is on its way: the send button becomes Stop. */
  isBusy: boolean
  autoFocus?: boolean
  /** Lets the chat hand focus back to the message box. */
  inputRef?: Ref<HTMLTextAreaElement>
}

const AssistantComposer = ({
  onSend,
  onStop,
  isBusy,
  autoFocus,
  inputRef,
}: AssistantComposerProps) => {
  const [text, setText] = useState('')
  const canSend = !isBusy && text.trim().length > 0

  const submit = () => {
    if (!canSend) return
    onSend(text.trim())
    setText('')
  }

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    submit()
  }

  // Enter sends; Shift+Enter starts a new line.
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      submit()
    }
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        px: 2,
        pt: 1.75,
        borderTop: 1,
        borderColor: 'divider',
        // Clear the home indicator on phones.
        pb: 'max(16px, env(safe-area-inset-bottom))',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.25 }}>
        <TextField
          value={text}
          onChange={event => setText(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about your cases or referrals…"
          inputProps={{ 'aria-label': `Message ${ASSISTANT_NAME}` }}
          autoFocus={autoFocus}
          inputRef={inputRef}
          multiline
          maxRows={6}
          fullWidth
          size="small"
        />
        {isBusy ? (
          <Tooltip title="Stop">
            <IconButton aria-label="Stop" onClick={onStop} sx={squareButtonSx}>
              <StopCircleOutlinedIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Send">
            <span>
              <IconButton
                aria-label="Send"
                type="submit"
                disabled={!canSend}
                sx={{
                  ...squareButtonSx,
                  bgcolor: canSend ? 'secondary.main' : 'action.hover',
                }}
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
      <Typography
        variant="caption"
        component="p"
        // text.disabled is ~2.7:1 on white; this line has to be readable.
        color="text.secondary"
        textAlign="center"
      >
        {ASSISTANT_DISCLAIMER}
      </Typography>
    </Box>
  )
}

export default AssistantComposer
